import * as React from 'react';

import { Button, Col, Row } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideRotateCcw } from 'lucide-react';

import { useUpdateRoleMutation } from './_graphql/useUpdateRoleMutation';

import {
  FormFooter,
  PermissionsEditor,
} from '~/modules/settings/_components/_shared';
import { allPermissionModules } from '~/modules/settings/_generated/permissions';
import {
  $bulkUpdateRolePermissions,
  $currentRole,
  $enabledPermissionKeys,
  $isRoleFormDirty,
  $originalRolePermissions,
  $resetRolePermissions,
  $rolePermissions,
  $updateRolePermission,
} from '~/modules/settings/_state/roleForm.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import { usePermission } from '~/shared/hooks/usePermission';

interface RolePermissionsTabProps {
  onRestore?: () => void;
  isRestorePending?: boolean;
}

export const RolePermissionsTab: React.FC<RolePermissionsTabProps> = ({
  onRestore,
  isRestorePending = false,
}) => {
  const t = useTranslations();
  const permissions = useAtomValue($rolePermissions);
  const isDirty = useAtomValue($isRoleFormDirty);
  const currentRole = useAtomValue($currentRole);
  const enabledKeys = useAtomValue($enabledPermissionKeys);
  const updatePermission = useSetAtom($updateRolePermission);
  const bulkUpdatePermissions = useSetAtom($bulkUpdateRolePermissions);
  const resetPermissions = useSetAtom($resetRolePermissions);
  const setOriginalPermissions = useSetAtom($originalRolePermissions);

  const updateRoleMutation = useUpdateRoleMutation();
  const isArchived = currentRole?.status === 'archived';
  const { allowed: canUpdatePermissions } = usePermission('permissionUpdate');
  const isReadOnly = isArchived || !canUpdatePermissions;

  const handlePermissionChange = React.useCallback(
    (key: string, enabled: boolean) => {
      updatePermission({ key, enabled });
    },
    [updatePermission],
  );

  const handleBulkChange = React.useCallback(
    (changes: Record<string, boolean>) => {
      bulkUpdatePermissions(changes);
    },
    [bulkUpdatePermissions],
  );

  const handleDiscard = React.useCallback(() => {
    resetPermissions();
  }, [resetPermissions]);

  const handleSave = React.useCallback(async () => {
    if (!currentRole) return;

    try {
      await updateRoleMutation.mutateAsync({
        id: currentRole.id,
        permissions: enabledKeys,
      });
      setOriginalPermissions({ ...permissions });
    } catch (error) {
      console.error('Failed to update role permissions:', error);
    }
  }, [
    currentRole,
    enabledKeys,
    permissions,
    updateRoleMutation,
    setOriginalPermissions,
  ]);

  return (
    <Col className="@RolePermissionsTab h-full">
      <div className="flex-1 overflow-y-auto p-5">
        <PermissionsEditor
          modules={allPermissionModules}
          value={permissions}
          onChange={isReadOnly ? undefined : handlePermissionChange}
          onBulkChange={isReadOnly ? undefined : handleBulkChange}
          readOnly={isReadOnly}
          readOnlyMessage={
            isArchived
              ? t.teamAndPermissions.roles.archivedReadOnlyMessage
              : undefined
          }
        />
      </div>
      {isArchived ? (
        <Row className="justify-end gap-2 border-t border-neutral/15 px-5 py-4">
          <Button
            icon={<LucideRotateCcw className="size-4" />}
            onPress={onRestore}
            isDisabled={isRestorePending}
          >
            {t.teamAndPermissions.roles.details.actions.restore}
          </Button>
        </Row>
      ) : (
        <FormFooter
          onDiscard={handleDiscard}
          onSave={handleSave}
          isDiscardDisabled={!isDirty}
          isSaveDisabled={!isDirty}
          isSaveLoading={updateRoleMutation.isPending}
          discardLabel={t.teamAndPermissions.actions.discard}
          saveLabel={t.teamAndPermissions.actions.save}
        />
      )}
    </Col>
  );
};
